import React from 'react'
import { useHistory } from 'react-router-dom'
import { EditOutlined, FileTextOutlined, SearchOutlined, WarningOutlined, WifiOutlined } from '@ant-design/icons'
import { useAppState } from '../../hooks/use-mobile-service'
import { MobileEmptyState } from '../../components/mobile-empty-state'

type EmptyVariant = 'filtered' | 'no-data' | 'error'

interface NotificationListEmptyProps {
    variant: EmptyVariant
    isDrafts: boolean
    /** Called when the retry button is clicked (variant='error' only) */
    onRetry?: () => void
}

/**
 * Contextual empty/error state for the notification/draft list.
 * Thin wrapper that resolves variant → MobileEmptyState props.
 */
export const NotificationListEmpty: React.FC<NotificationListEmptyProps> = ({ variant, isDrafts, onRetry }) => {
    const appState = useAppState()
    const history = useHistory()
    const domainId = appState.currentDomain?.id

    switch (variant) {
        case 'error':
            return (
                <MobileEmptyState
                    icon={<WarningOutlined className="mobile-empty-state__icon--error" />}
                    description={`Failed to load ${isDrafts ? 'drafts' : 'notifications'}`}
                    action={onRetry ? { label: 'Retry', onClick: onRetry, type: 'default' } : undefined}
                />
            )
        case 'filtered':
            return (
                <MobileEmptyState
                    icon={<SearchOutlined />}
                    description={`No ${isDrafts ? 'drafts' : 'notifications'} match your filters`}
                />
            )
        case 'no-data':
        default:
            return (
                <MobileEmptyState
                    icon={isDrafts ? <FileTextOutlined /> : <WifiOutlined />}
                    description={isDrafts ? 'No drafts yet' : 'No notifications yet'}
                    action={{
                        label: 'Start Composing',
                        onClick: () => domainId && history.push(`/domains/${domainId}/notifications/new`),
                        icon: <EditOutlined />,
                    }}
                />
            )
    }
}
